import React from 'react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { Modal } from '../index';
import { TeacherSchedule, EditFormData } from '../../types/schedule';

interface ScheduleDetailModalProps {
  schedule: TeacherSchedule | null;
  editingSchedule: TeacherSchedule | null;
  editForm: EditFormData;
  setEditForm: React.Dispatch<React.SetStateAction<EditFormData>>;
  onClose: () => void;
  onSubmitEdit: () => void;
  onDelete: () => void;
}

// 개인일정 상세/수정 모달 — RescheduleModal(일정 변경)과 동일한 디자인 언어 사용
const ScheduleDetailModal: React.FC<ScheduleDetailModalProps> = ({
  schedule,
  editingSchedule,
  editForm,
  setEditForm,
  onClose,
  onSubmitEdit,
  onDelete
}) => {
  if (!schedule) return null;

  const studentName = (schedule as any).user?.name || schedule.studentName;
  const notes = (schedule as any).notes;

  const recurringText = schedule.isRecurring
    ? (() => {
        if (schedule.recurringPattern === 'daily') return '매일';
        if (schedule.recurringPattern === 'weekly') {
          const weekdayLabels = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
          return `매주 ${weekdayLabels[new Date(schedule.scheduleDate).getDay()]}`;
        }
        if (schedule.recurringPattern === 'monthly') return `매월 ${new Date(schedule.scheduleDate).getDate()}일`;
        return '반복';
      })() + (schedule.recurringUntil ? ` ~ ${format(new Date(schedule.recurringUntil), 'yyyy년 M월 d일')}` : '')
    : null;

  return (
    <Modal isOpen={!!schedule} onClose={onClose} title="개인일정 상세" size="sm">
      <div className="reschedule-modal-content">
        {/* 뱃지 + 제목 (일정 변경 모달과 동일한 헤더) */}
        <div className="reschedule-info">
          <div className="schedule-type-badge" style={{ backgroundColor: '#64748b' }}>
            개인 일정
          </div>
          <h3 className="schedule-title">{schedule.title || '개인일정'}</h3>
        </div>

        {/* 현재 일정 정보 패널 */}
        <div className="reschedule-dates">
          <div className="date-row">
            <span className="date-label">일정</span>
            <span className="date-value new">
              {format(new Date(schedule.scheduleDate), 'M월 d일 (E)', { locale: ko })}
              <span className="time">
                {schedule.startTime
                  ? `${schedule.startTime.substring(0, 5)} - ${schedule.endTime?.substring(0, 5) ?? ''}`
                  : '종일'}
              </span>
            </span>
          </div>
          {studentName && (
            <div className="date-row" style={{ marginTop: 8 }}>
              <span className="date-label">학생</span>
              <span className="date-value original">{studentName}</span>
            </div>
          )}
          {recurringText && (
            <div className="date-row" style={{ marginTop: 8 }}>
              <span className="date-label">반복</span>
              <span className="date-value original">{recurringText}</span>
            </div>
          )}
          {notes && (
            <div className="date-row" style={{ marginTop: 8 }}>
              <span className="date-label">메모</span>
              <span className="date-value original">{notes}</span>
            </div>
          )}
        </div>

        {/* 수정 폼 */}
        {editingSchedule && (
          <>
            <div className="time-change-section">
              <label className="section-label">제목</label>
              <div className="time-inputs">
                <input
                  className="time-input"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder="일정 제목"
                />
              </div>
            </div>

            <div className="time-change-section">
              <label className="section-label">날짜</label>
              <div className="time-inputs">
                <input
                  type="date"
                  className="time-input"
                  value={editForm.scheduleDate}
                  onChange={(e) => setEditForm({ ...editForm, scheduleDate: e.target.value })}
                />
              </div>
            </div>

            <div className="time-change-section">
              <label className="section-label">시간</label>
              <div className="time-inputs">
                <input
                  type="time"
                  className="time-input"
                  value={editForm.startTime}
                  onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
                />
                <span className="time-separator">~</span>
                <input
                  type="time"
                  className="time-input"
                  value={editForm.endTime}
                  onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
                />
              </div>
            </div>

            <div className="time-change-section">
              <label className="section-label">설명</label>
              <div className="time-inputs">
                <textarea
                  className="time-input"
                  rows={2}
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="설명 (선택)"
                  style={{ resize: 'vertical' }}
                />
              </div>
            </div>
          </>
        )}

        <div className="modal-buttons">
          <button className="btn btn-secondary" onClick={onClose}>
            취소
          </button>
          <button className="btn btn-danger" onClick={onDelete}>
            삭제
          </button>
          {editingSchedule && (
            <button className="btn btn-primary" onClick={onSubmitEdit}>
              수정 저장
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ScheduleDetailModal;
